import { useEffect, useRef, useState } from 'react';

import type { ComponentType, ElementType } from 'react';

export type SwitchedComponentProps = {
	visible: boolean;
};

export type SwitchableContextConfig = {
	/**
	 * Set to true to disable all views
	 */
	hideAll?: boolean;

	/**
	 * Max number of views to keep loaded in the background. Defaults to all
	 * views remaining open in background.
	 */
	maxBackgroundViews?: number;

	/**
	 * A period of time, after which background views will be unloaded. Unset or 0
	 * disables timeouts.
	 */
	backgroundTimeoutMs?: number;
};

interface SwitchedView {
	viewId: string | number;
	unloadTimer?: number;
	lastOpenTimeMs: number;
}

export type SwitchableViewsProps<T> = SwitchableContextConfig & {
	ViewComponent: ComponentType<T & SwitchedComponentProps>;
	viewProps: { [viewId: string]: T };
	selectedViewId?: string | undefined;
	DefaultViewComponent?: ElementType | undefined;
};

/**
 * The switchable views component works like a tab container. However, when a new view becomes active
 * this component will manage the state of the old view and, subject to configuration settings,
 * keep old views loaded up to a maximum number and time limit.
 *
 * Child components are passed a `visible` boolean and should hide themselves from view when not visible.
 */
export function SwitchableViews<T>({
	hideAll,
	maxBackgroundViews,
	backgroundTimeoutMs,
	viewProps,
	ViewComponent,
	selectedViewId,
	DefaultViewComponent,
}: SwitchableViewsProps<T>) {
	const [loadedViews, setLoadedViews] = useState<{ [viewId: string]: SwitchedView }>({});
	const loadedViewsRef = useRef(loadedViews);
	loadedViewsRef.current = loadedViews;

	if (typeof maxBackgroundViews === 'number' && maxBackgroundViews < 0) {
		throw new Error('maxBackgroundViews must be >= 0');
	}

	if (typeof backgroundTimeoutMs === 'number' && backgroundTimeoutMs < 0) {
		throw new Error('backgroundTimeoutMs must be >= 0');
	}

	useEffect(() => {
		if (typeof selectedViewId === 'undefined') {
			return;
		}

		let doUpdates = true;

		// If we have a max number of views to keep in the background, unload the oldest ones
		if (typeof maxBackgroundViews === 'number') {
			const sorted = Object.values(loadedViewsRef.current).sort((a, b) => a.lastOpenTimeMs - b.lastOpenTimeMs);
			let numLoaded = sorted.length;
			if (!loadedViewsRef.current[selectedViewId]) {
				numLoaded++;
			}

			for (let i = 0; i < numLoaded - maxBackgroundViews && i < sorted.length; ++i) {
				if (selectedViewId === sorted[i]!.viewId) {
					continue;
				}

				// eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- Legacy code
				delete loadedViewsRef.current[sorted[i]!.viewId];
			}
		}

		// If we have background timeouts, setup and manage the timeouts
		if (backgroundTimeoutMs !== undefined) {
			for (const view of Object.values(loadedViewsRef.current)) {
				if (view.viewId !== selectedViewId) {
					if (typeof view.unloadTimer === 'undefined') {
						view.unloadTimer = window.setTimeout(() => {
							if (doUpdates && loadedViewsRef.current[view.viewId] && view.viewId !== selectedViewId) {
								// eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- Legacy code
								delete loadedViewsRef.current[view.viewId];
								setLoadedViews({ ...loadedViewsRef.current });
							}
						}, backgroundTimeoutMs);
					}
				} else if (typeof view.unloadTimer !== 'undefined') {
					clearTimeout(view.unloadTimer);
					delete view.unloadTimer;
				}
			}
		}

		// Add currently selected view id and/or set last open time
		if (!loadedViewsRef.current[selectedViewId]) {
			loadedViewsRef.current[selectedViewId] = {
				viewId: selectedViewId,
				lastOpenTimeMs: Date.now(),
			};
		} else {
			loadedViewsRef.current[selectedViewId].lastOpenTimeMs = Date.now();
		}

		setLoadedViews({ ...loadedViewsRef.current });
		return () => {
			doUpdates = false;
			for (const view of Object.values(loadedViewsRef.current)) {
				if (typeof view.unloadTimer !== 'undefined') {
					clearTimeout(view.unloadTimer);
					delete view.unloadTimer;
				}
			}
		};
	}, [backgroundTimeoutMs, maxBackgroundViews, selectedViewId]);

	if (selectedViewId === undefined && DefaultViewComponent !== undefined) {
		return <DefaultViewComponent />;
	}

	return Object.keys(viewProps).map((viewId) => {
		if (!loadedViews[viewId] && selectedViewId !== viewId) {
			return null;
		}

		return <ViewComponent key={viewId} {...viewProps[viewId]!} visible={!hideAll && selectedViewId === viewId} />;
	});
}
